import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { userService } from '../../services/api';
import { FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import { format, parseISO } from 'date-fns';
import './PersonalDetails.css';

const EMPLOYMENT_TYPES = ['Full-Time', 'Part-Time', 'Contract', 'Temporary', 'Intern'];

export const PersonalDetails = () => {
  const { user } = useAuth();
  const isReadOnly = user?.isActive === false;
  const [profileData, setProfileData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    personal: true,
    employment: true,
    emergency: true,
    nin: false,
  });

  const [editData, setEditData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    address: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    nationalInsuranceNumber: '',
  });

  const [employmentEditData, setEmploymentEditData] = useState({
    jobTitle: '',
    department: '',
    employmentType: '',
    joinDate: '',
    managerName: '',
  });

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await userService.getById(user?.id);
      setProfileData(response.data);
      initializeEditData(response.data);
    } catch (err) {
      setError('Failed to load profile data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const initializeEditData = (data) => {
    setEditData({
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      email: data.email || '',
      phone: data.phone || '',
      dateOfBirth: data.dateOfBirth || '',
      address: data.address || '',
      emergencyContactName: data.emergencyContactName || '',
      emergencyContactPhone: data.emergencyContactPhone || '',
      nationalInsuranceNumber: data.nationalInsuranceNumber || '',
    });

    setEmploymentEditData({
      jobTitle: data.jobTitle || '',
      department: data.Department?.name || '',
      employmentType: data.employmentType || '',
      joinDate: data.joinDate || '',
      managerName: data.manager ? `${data.manager.firstName} ${data.manager.lastName}` : '',
    });
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEmploymentEditChange = (e) => {
    const { name, value } = e.target;
    setEmploymentEditData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateData = () => {
    const errors = [];

    if (!editData.firstName.trim()) {
      errors.push('First name is required');
    }

    if (!editData.lastName.trim()) {
      errors.push('Last name is required');
    }

    if (!editData.email.trim()) {
      errors.push('Email is required');
    }

    if (!editData.phone.trim()) {
      errors.push('Phone number is required');
    }

    if (editData.emergencyContactPhone && !editData.emergencyContactName) {
      errors.push('Emergency contact name is required if phone is provided');
    }

    return errors;
  };

  const handleSaveChanges = async () => {
    const errors = validateData();
    if (errors.length > 0) {
      setError(errors.join('\n'));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        ...editData,
        ...(user?.role === 'admin' || user?.role === 'manager' ? employmentEditData : {}),
      };

      await userService.update(user?.id, payload);
      setSuccess('Profile updated successfully');
      setIsEditMode(false);
      fetchProfileData();
    } catch (err) {
      setError('Failed to save changes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    initializeEditData(profileData);
    setError(null);
  };

  if (loading && !profileData) {
    return <div className="loading-page">Loading profile...</div>;
  }

  if (!profileData) {
    return <div className="error-page">Failed to load profile data</div>;
  }

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  return (
    <div className="personal-details-container">
      <div className="page-header">
        <h1>Personal Details</h1>
        {!isReadOnly && !isEditMode && (
          <button
            className="btn btn-primary"
            onClick={() => setIsEditMode(true)}
          >
            <FiEdit2 /> Edit Profile
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-error">
          {error.split('\n').map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}

      {success && <div className="alert alert-success">{success}</div>}

      {/* Profile Card */}
      <div className="profile-card">
        <div className="profile-header">
          <div className="profile-avatar">
            {profileData.firstName?.charAt(0).toUpperCase()}
            {profileData.lastName?.charAt(0).toUpperCase()}
          </div>
          <div className="profile-info">
            <h2>
              {profileData.firstName} {profileData.lastName}
            </h2>
            <p className="job-title">{profileData.jobTitle}</p>
            <p className="department">{profileData.Department?.name || ''}</p>
          </div>
        </div>
      </div>

      {/* Personal Information Section */}
      <div className="details-section">
        <button
          className="section-header"
          onClick={() => toggleSection('personal')}
        >
          <span>Personal Information</span>
          <span className={`toggle-icon ${expandedSections.personal ? 'open' : ''}`}>
            ▼
          </span>
        </button>

        {expandedSections.personal && (
          <div className="section-content">
            <div className="form-grid">
              <div className="form-group">
                <label>First Name</label>
                {isEditMode ? (
                  <input
                    type="text"
                    name="firstName"
                    value={editData.firstName}
                    onChange={handleEditChange}
                    disabled={loading}
                  />
                ) : (
                  <p className="display-value">{profileData.firstName}</p>
                )}
              </div>

              <div className="form-group">
                <label>Last Name</label>
                {isEditMode ? (
                  <input
                    type="text"
                    name="lastName"
                    value={editData.lastName}
                    onChange={handleEditChange}
                    disabled={loading}
                  />
                ) : (
                  <p className="display-value">{profileData.lastName}</p>
                )}
              </div>

              <div className="form-group">
                <label>Email</label>
                {isEditMode ? (
                  <input
                    type="email"
                    name="email"
                    value={editData.email}
                    onChange={handleEditChange}
                    disabled={loading}
                  />
                ) : (
                  <p className="display-value">{profileData.email}</p>
                )}
              </div>

              <div className="form-group">
                <label>Phone</label>
                {isEditMode ? (
                  <input
                    type="tel"
                    name="phone"
                    value={editData.phone}
                    onChange={handleEditChange}
                    disabled={loading}
                  />
                ) : (
                  <p className="display-value">{profileData.phone}</p>
                )}
              </div>

              <div className="form-group">
                <label>Date of Birth</label>
                {isEditMode ? (
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={editData.dateOfBirth}
                    onChange={handleEditChange}
                    disabled={loading}
                  />
                ) : (
                  <p className="display-value">
                    {profileData.dateOfBirth
                      ? format(parseISO(profileData.dateOfBirth), 'MMM d, yyyy')
                      : 'Not provided'}
                  </p>
                )}
              </div>

              <div className="form-group full-width">
                <label>Address</label>
                {isEditMode ? (
                  <textarea
                    name="address"
                    value={editData.address}
                    onChange={handleEditChange}
                    disabled={loading}
                    rows={3}
                  />
                ) : (
                  <p className="display-value">{profileData.address || 'Not provided'}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Employment Information Section */}
      <div className="details-section">
        <button
          className="section-header"
          onClick={() => toggleSection('employment')}
        >
          <span>Employment Information</span>
          <span className={`toggle-icon ${expandedSections.employment ? 'open' : ''}`}>
            ▼
          </span>
        </button>

        {expandedSections.employment && (
          <div className="section-content">
            <div className={isEditMode && isAdmin ? 'read-only-notice' : ''}>
              {isEditMode && !isAdmin && (
                <p className="read-only-text">Employment information is read-only</p>
              )}
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Job Title</label>
                {isEditMode && isAdmin ? (
                  <input
                    type="text"
                    name="jobTitle"
                    value={employmentEditData.jobTitle}
                    onChange={handleEmploymentEditChange}
                    disabled={loading}
                  />
                ) : (
                  <p className="display-value">{profileData.jobTitle}</p>
                )}
              </div>

              <div className="form-group">
                <label>Department</label>
                {isEditMode && isAdmin ? (
                  <input
                    type="text"
                    name="department"
                    value={employmentEditData.department}
                    onChange={handleEmploymentEditChange}
                    disabled={loading}
                  />
                ) : (
                  <p className="display-value">{profileData.Department?.name || ''}</p>
                )}
              </div>

              <div className="form-group">
                <label>Employment Type</label>
                {isEditMode && isAdmin ? (
                  <select
                    name="employmentType"
                    value={employmentEditData.employmentType}
                    onChange={handleEmploymentEditChange}
                    disabled={loading}
                  >
                    <option value="">Select type</option>
                    {EMPLOYMENT_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="display-value">{profileData.employmentType}</p>
                )}
              </div>

              <div className="form-group">
                <label>Join Date</label>
                {isEditMode && isAdmin ? (
                  <input
                    type="date"
                    name="joinDate"
                    value={employmentEditData.joinDate}
                    onChange={handleEmploymentEditChange}
                    disabled={loading}
                  />
                ) : (
                  <p className="display-value">
                    {profileData.joinDate
                      ? format(parseISO(profileData.joinDate), 'MMM d, yyyy')
                      : 'Not provided'}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label>Manager</label>
                {isEditMode && isAdmin ? (
                  <input
                    type="text"
                    name="managerName"
                    value={employmentEditData.managerName}
                    onChange={handleEmploymentEditChange}
                    disabled={loading}
                  />
                ) : (
                  <p className="display-value">{profileData.manager ? `${profileData.manager.firstName} ${profileData.manager.lastName}` : 'Not assigned'}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Emergency Contact Section */}
      <div className="details-section">
        <button
          className="section-header"
          onClick={() => toggleSection('emergency')}
        >
          <span>Emergency Contact</span>
          <span className={`toggle-icon ${expandedSections.emergency ? 'open' : ''}`}>
            ▼
          </span>
        </button>

        {expandedSections.emergency && (
          <div className="section-content">
            <div className="form-grid">
              <div className="form-group">
                <label>Contact Name</label>
                {isEditMode ? (
                  <input
                    type="text"
                    name="emergencyContactName"
                    value={editData.emergencyContactName}
                    onChange={handleEditChange}
                    disabled={loading}
                  />
                ) : (
                  <p className="display-value">{profileData.emergencyContactName || 'Not provided'}</p>
                )}
              </div>

              <div className="form-group">
                <label>Contact Phone</label>
                {isEditMode ? (
                  <input
                    type="tel"
                    name="emergencyContactPhone"
                    value={editData.emergencyContactPhone}
                    onChange={handleEditChange}
                    disabled={loading}
                  />
                ) : (
                  <p className="display-value">{profileData.emergencyContactPhone || 'Not provided'}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* National Insurance Number Section */}
      <div className="details-section">
        <button
          className="section-header"
          onClick={() => toggleSection('nin')}
        >
          <span>National Insurance Number</span>
          <span className={`toggle-icon ${expandedSections.nin ? 'open' : ''}`}>
            ▼
          </span>
        </button>

        {expandedSections.nin && (
          <div className="section-content">
            <div className="form-grid">
              <div className="form-group full-width">
                <label>NI Number</label>
                {isEditMode ? (
                  <input
                    type="text"
                    name="nationalInsuranceNumber"
                    value={editData.nationalInsuranceNumber}
                    onChange={handleEditChange}
                    disabled={loading}
                    placeholder="XX 99 99 99 X"
                  />
                ) : (
                  <p className="display-value">{profileData.nationalInsuranceNumber || 'Not provided'}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {isEditMode && (
        <div className="action-buttons">
          <button
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={loading}
          >
            <FiX /> Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSaveChanges}
            disabled={loading}
          >
            <FiCheck /> {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
};

export default PersonalDetails;
